import { useNavigate } from "react-router-dom";
import { ListChecks, SealCheck, Scales, ShieldCheck, Flag, CheckCircle, CaretRight } from "@phosphor-icons/react";

// A visible month-end path so users always know where they are and what's next:
// Review → Post → Reconcile → Approve → Sign-off. Derived from /dashboard/overview.
function buildSteps(d) {
  const overdue = d.journals_overdue?.count || 0;
  const due = d.journals_due?.count || 0;
  // Postable now (matches the Close page + scheduler), not just "due later this month".
  const post = d.journals_to_post != null ? d.journals_to_post : (overdue + due);
  const recon = d.reconciliation || {};
  return [
    { key: "review", label: "Review", icon: ListChecks, count: d.unscheduled_count || 0,
      done: (d.unscheduled_count || 0) === 0, to: "/unscheduled",
      hint: "Schedule imported balances" },
    { key: "post", label: "Post", icon: SealCheck, count: post, urgent: overdue > 0,
      done: post === 0, to: "/close", hint: "Release journals to Xero",
      dueLater: d.journals_due_later || 0 },
    { key: "reconcile", label: "Reconcile", icon: Scales,
      count: d.reconciliation_variances || 0,
      done: (d.reconciliation_variances || 0) === 0 && (recon.total || 0) > 0,
      neutral: (recon.total || 0) === 0, to: "/reconciliation",
      hint: "Tie balances to the register" },
    { key: "approve", label: "Approve", icon: ShieldCheck, count: d.approvals_waiting || 0,
      done: (d.approvals_waiting || 0) === 0, to: "/approvals",
      hint: "Second-pair-of-eyes sign-off" },
    { key: "signoff", label: "Sign-off", icon: Flag, count: 0,
      done: !!(d.journey_signed_off ?? d.month_signed_off), to: "/close", hint: "Lock the period" },
  ];
}

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
function monthLabel(m) {
  if (!m || typeof m !== "string" || !m.includes("-")) return "";
  const [y, mo] = m.split("-");
  const name = MONTHS[parseInt(mo, 10) - 1];
  return name ? `${name} ${y}` : "";
}

export default function MonthEndJourney({ data, onStep, postCount }) {
  const nav = useNavigate();
  if (!data || !data.has_tenant) return null;
  const steps = buildSteps(data);
  // Optional override so the Close page's "Post" step matches its "postable now" figure.
  if (postCount != null) {
    const p = steps.find((s) => s.key === "post");
    if (p) { p.count = postCount; p.done = postCount === 0; p.urgent = postCount > 0 && p.urgent; }
  }
  // The "current" step is the first that isn't done (skipping neutral reconcile with no data).
  const currentIdx = steps.findIndex((s) => !s.done && !s.neutral);
  const doneCount = steps.filter((s) => s.done).length;
  const go = (s) => { if (onStep) onStep(s); else nav(s.to); };

  return (
    <div data-testid="month-end-journey"
      className="rounded-2xl border border-slate-200 bg-white shadow-card px-5 py-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="overline text-orangeink">Month-end path</span>
          {monthLabel(data.journey_month) && (
            <span className="text-xs font-semibold text-navy/70" data-testid="journey-month">· {monthLabel(data.journey_month)}</span>
          )}
          <span className="text-xs text-navy/55">{doneCount}/{steps.length} done</span>
        </div>
        {currentIdx === -1 && (
          <span data-testid="journey-complete" className="inline-flex items-center gap-1.5 text-sm font-semibold text-positive">
            <CheckCircle size={16} weight="fill" /> Ready to close
          </span>
        )}
      </div>
      <div className="flex items-stretch gap-1.5 overflow-x-auto">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const isCurrent = i === currentIdx;
          const state = s.done ? "done" : isCurrent ? "current" : s.neutral ? "neutral" : "todo";
          const styles = {
            done: "border-positive/30 bg-positive/[0.06]",
            current: s.urgent ? "border-negative/50 bg-negative/[0.05] ring-1 ring-negative/30" : "border-orange bg-orange/[0.06] ring-1 ring-orange/25",
            neutral: "border-slate-200 bg-white",
            todo: "border-slate-200 bg-white hover:border-navy/30",
          }[state];
          return (
            <button key={s.key} onClick={() => go(s)} data-testid={`journey-step-${s.key}`}
              className={`group relative flex-1 min-w-[104px] text-left rounded-xl border px-3 py-2.5 transition-all ${styles}`}>
              <div className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                  s.done ? "bg-positive/15 text-positive" : isCurrent ? (s.urgent ? "bg-negative/15 text-negative" : "bg-orange/15 text-orangeink") : "bg-navy/[0.06] text-navy/55"}`}>
                  {s.done ? <CheckCircle size={16} weight="fill" /> : <Icon size={16} weight={isCurrent ? "fill" : "regular"} />}
                </span>
                <div className="min-w-0">
                  <div className={`text-sm font-semibold leading-tight ${s.done ? "text-navy/55" : "text-navy"}`}>{s.label}</div>
                  {s.count > 0
                    ? <div className={`text-[0.7rem] font-bold tabular ${s.urgent ? "text-negative" : "text-orangeink"}`} data-testid={`journey-count-${s.key}`}>{s.count} to do</div>
                    : <div className="text-[0.7rem] text-navy/40">{s.done ? "Clear" : (s.neutral ? "No data" : "Waiting")}</div>}
                  {s.dueLater > 0 && (
                    <div className="text-[0.65rem] text-navy/40 leading-tight" data-testid={`journey-duelater-${s.key}`} title="Dated later this month — not postable yet">
                      {s.dueLater} due later
                    </div>
                  )}
                </div>
              </div>
              {i < steps.length - 1 && (
                <CaretRight size={12} weight="bold" className="hidden md:block absolute -right-[9px] top-1/2 -translate-y-1/2 z-10 text-slate-300" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
